import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import MediaCard from '@/components/feature/MediaCard';
import EpisodeList from '@/pages/detail/components/EpisodeList';
import ShareModal from '@/pages/detail/components/ShareModal';
import { useFavorites } from '@/hooks/useFavorites';
import { useHistory } from '@/hooks/useHistory';
import { getMediaById, getRelatedMedia } from '@/utils/media';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getMediaById(id) : undefined;
  const { isFavorite, toggleFavorite } = useFavorites();
  const { addHistory } = useHistory();

  const [episode, setEpisode] = useState(1);
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    if (item) addHistory(item.id);
  }, [item, addHistory]);

  useEffect(() => {
    setEpisode(1);
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(''), 2600);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!item) {
    return (
      <div className="mx-auto flex w-full flex-col items-center justify-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
          <i className="ri-film-line text-3xl"></i>
        </span>
        <h1 className="mt-4 text-xl font-bold text-foreground-800">没有找到该影视资源</h1>
        <p className="mt-1 text-sm text-foreground-500">资源可能已被移除，去看看其他内容吧</p>
        <Link
          to="/"
          className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const related = getRelatedMedia(item, 4);
  const faved = isFavorite(item.id);

  const infoRows = [
    { label: '类型', value: item.type === 'movie' ? '电影' : '电视剧' },
    { label: '导演', value: item.director },
    { label: '主演', value: item.cast.join(' / ') },
    { label: '年份', value: String(item.year) },
    { label: '地区', value: item.region },
    { label: '题材', value: item.genres.join(' · ') },
  ];

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6">
      <nav className="mb-5 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to={`/category/${item.type}`} className="hover:text-primary-600">
          {item.type === 'movie' ? '电影' : '电视剧'}
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">{item.title}</span>
      </nav>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
            <div className="relative aspect-[2/3] w-full bg-background-200">
              <img
                src={item.cover}
                alt={`${item.title} 高清海报`}
                title={`${item.title} 影视详情`}
                className="h-full w-full object-cover object-top"
              />
              <span className="absolute left-3 top-3 rounded-full bg-accent-500 px-3 py-1 text-sm font-bold text-background-50">
                {item.rating.toFixed(1)} 分
              </span>
            </div>
            <div className="flex flex-col gap-3 p-4">
              <button
                onClick={() => setToast('该资源暂不支持在线播放，敬请期待更多精彩内容')}
                className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 py-3 text-sm font-medium text-background-50 hover:bg-primary-600"
              >
                <i className="ri-play-circle-fill text-lg"></i> 立即观看
              </button>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    const now = toggleFavorite(item.id);
                    setToast(now ? '已加入收藏' : '已取消收藏');
                  }}
                  className={`flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md border py-2.5 text-sm font-medium transition-colors ${
                    faved
                      ? 'border-accent-300 bg-accent-50 text-accent-700'
                      : 'border-background-300 text-foreground-700 hover:border-accent-300 hover:text-accent-600'
                  }`}
                >
                  <i className={faved ? 'ri-heart-3-fill' : 'ri-heart-3-line'}></i>
                  {faved ? '已收藏' : '收藏'}
                </button>
                <button
                  onClick={() => setShareOpen(true)}
                  className="flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 py-2.5 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-share-forward-line"></i> 分享
                </button>
              </div>
              <p className="rounded-md bg-background-100 p-3 text-xs leading-relaxed text-foreground-500">
                说明：本站为演示平台，播放按钮仅作样式展示，不含实际播放功能。
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-6 lg:col-span-2">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">{item.title}</h1>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {item.genres.map((g) => (
                    <span key={g} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                      {g}
                    </span>
                  ))}
                  <span className="rounded-full bg-primary-100 px-3 py-1 text-xs text-primary-700">
                    {item.type === 'movie' ? '电影' : '电视剧'}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-primary-600">{item.rating.toFixed(1)}</p>
                <p className="text-xs text-foreground-500">综合评分</p>
              </div>
            </div>

            <dl className="mt-5 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
              {infoRows.map((row) => (
                <div key={row.label} className="flex gap-3 text-sm">
                  <dt className="w-12 shrink-0 text-foreground-500">{row.label}</dt>
                  <dd className="text-foreground-800">{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <section className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h2 className="mb-3 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-file-text-line mr-2 text-primary-500"></i>剧情简介
            </h2>
            <p className="text-sm leading-relaxed text-foreground-700">{item.synopsis}</p>
            <p className="mt-4 text-xs text-foreground-400">
              更新时间：{item.updatedAt} · 热度：{item.hits.toLocaleString()}
            </p>
          </section>

          {item.type === 'tv' && item.episodeCount && (
            <EpisodeList
              count={item.episodeCount}
              current={episode}
              status={item.status}
              onSelect={(n) => {
                setEpisode(n);
                setToast(`已切换至第 ${n} 集（演示效果）`);
              }}
            />
          )}

          <section>
            <h2 className="mb-4 font-heading text-lg font-bold text-foreground-950">
              <i className="ri-magic-line mr-2 text-accent-500"></i>相关推荐
            </h2>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
              {related.map((rel) => (
                <MediaCard key={rel.id} item={rel} showFav={false} />
              ))}
            </div>
          </section>
        </div>
      </div>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} title={item.title} />

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[70] -translate-x-1/2 animate-fade-up">
          <div className="flex items-center gap-2 rounded-full bg-foreground-900 px-5 py-3 text-sm text-background-50">
            <i className="ri-information-line text-lg text-primary-300"></i>
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}